import {
  AfterViewInit,
  Component,
  ElementRef, Input,
  OnInit,
  ViewChild
} from '@angular/core';
import {Store} from "@ngrx/store";
import {AppState} from "../../store";
import {NavigationEnd, NavigationStart, Router} from "@angular/router";
import {combineLatest, Subscription, take} from "rxjs";
import {DisplayService} from "../../services/display.service";
import {setJobFilter} from "../../store/actions/filter.actions";
import {getFilterByKey} from "../../store/selectors/filter.selector";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {getSelfAssessedRequirements} from "../../store/selectors/user.selector";

@UntilDestroy()
@Component({
  selector: 'brt-sub-navigation',
  templateUrl: './sub-navigation.component.html',
  styleUrls: ['./sub-navigation.component.scss']
})
export class SubNavigationComponent implements OnInit, AfterViewInit {

  selectLabel = "A–Z";

  sortOptions: {value: string, label: string, disabled: boolean}[] = []

  @ViewChild("selectElement") set setSelectElement(val: ElementRef) {
    if(val) {
      this.selectElement = val;

      this.setFilterKeyByUrl(window.location.href);
      this.fillSortOptions();
      window.requestAnimationFrame(() => {
        this.store.select(getFilterByKey(this.filterKey)).pipe(take(1)).subscribe((val) => {
          const htmlEl = this.selectElement.nativeElement as HTMLSelectElement;
          // htmlEl.value = val;
          this.selectValue = val;
          this.setSelectWidth();
        })
      });

    }
  }

  filterKey = "archiveSortBy";
  currentStoreUpdateListener?: Subscription;

  @Input('showArchiveSort') showArchiveSort = false;
  selectElement: ElementRef = new ElementRef(null);
  selectValue = "";

  requirementsTestDone = false;

  isLocationFilterProviderSet = false;
  isLocationFilterJobSet = false;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private displayService: DisplayService
  ) {
  }

  ngOnInit() {

    const clSrcObject = {
      activeDistrictIdsProvider: this.store.select(getFilterByKey("activeProviderDistrictIds")),
      searchTermLocationProvider: this.store.select(getFilterByKey("searchTermProviderLocation")),
      activeDistrictIdsJobs: this.store.select(getFilterByKey("activeJobDistrictIds")),
      searchTermLocationJobs: this.store.select(getFilterByKey("searchTermJobLocation")),
    };

    this.store.select(getSelfAssessedRequirements()).pipe(untilDestroyed(this)).subscribe((selfRequirementsRows) => {
      let hasRequirements = false;
      for(let i = 0; i < selfRequirementsRows.length; i++) {
        if(selfRequirementsRows[i].reqIds.length) {
          hasRequirements = true;
          break;
        }
      }
      this.requirementsTestDone = hasRequirements;
      this.fillSortOptions();
    })

    this.router.events
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
        } else if (event instanceof NavigationEnd) {

          // Set Default after Page Changed IF no filter in url set
          if(event.url.indexOf("#") === -1) {
            this.store.dispatch(setJobFilter({key: "archiveSortBy", value: "name-asc"}))
            this.store.dispatch(setJobFilter({key: "jobsArchiveSortBy", value: "name-asc"}))
            this.store.dispatch(setJobFilter({key: "providersArchiveSortBy", value: "name-asc"}))
          }
          this.fillSortOptions();

          setTimeout(() => {
            this.setFilterKeyByUrl(event.url);

            if(this.currentStoreUpdateListener) {
              this.currentStoreUpdateListener.unsubscribe();
            }

            this.currentStoreUpdateListener = this.store.select(getFilterByKey(this.filterKey)).pipe(untilDestroyed(this)).subscribe((val) => {
              if(this.selectElement && this.selectElement.nativeElement) {
                // console.log(val);

                const htmlEl = this.selectElement.nativeElement as HTMLSelectElement;
                // htmlEl.value = val;
                this.selectValue = val;
                this.setSelectWidth();
              }
            });
          }, 300);


        }
      });


    combineLatest(clSrcObject).pipe(untilDestroyed(this)).subscribe(async ({activeDistrictIdsProvider, searchTermLocationProvider, activeDistrictIdsJobs, searchTermLocationJobs}) => {
      this.isLocationFilterProviderSet = !(activeDistrictIdsProvider.length === 0 && searchTermLocationProvider.length === 0);
      this.isLocationFilterJobSet = !(activeDistrictIdsJobs.length === 0 && searchTermLocationJobs.length === 0);

      // console.log(this.isLocationFilterProviderSet, this.filterKey, this.isLocationFilterJobSet);

      if((!this.isLocationFilterProviderSet && this.filterKey === "providersArchiveSortBy") || (!this.isLocationFilterJobSet && this.filterKey === "jobsArchiveSortBy")) {

        this.store.select(getFilterByKey(this.filterKey)).pipe(take(1)).subscribe((val) => {
          if(val === "distance") {
            // console.warn("reset to name-asc", this.filterKey);
            this.store.dispatch(setJobFilter({key: this.filterKey, value: "name-asc"}));
          }
        });
      }

      this.fillSortOptions();
      window.requestAnimationFrame(() => {
        this.setSelectWidth();
      });
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setSelectWidth();
    }, 100);
    this.displayService.onWindowResize().pipe(untilDestroyed(this)).subscribe((nextVal) => {
      if(this.showArchiveSort) {
        this.setSelectWidth();
      }
    })
  }

  setFilterKeyByUrl(url: string) {
    if(url.indexOf("anbieter") > -1) {
      this.filterKey = "providersArchiveSortBy"
    } else if(url.indexOf("berufe") > -1) {
      this.filterKey = "archiveSortBy"
    } else if(url.indexOf("jobboerse") > -1) {
      this.filterKey = "jobsArchiveSortBy"
    }
  }

  fillSortOptions() {
    const url = window.location.href;
    this.sortOptions = [];

    if(url.indexOf("anbieter") > -1) {
      this.sortOptions = [
        {
          label: "Näheste Entfernung",
          value: "distance",
          disabled: !this.isLocationFilterProviderSet
        },
        {
          label: "Offene Lehrstellen",
          value: "open-positions",
          disabled: false
        }
      ];
    }

    if(url.indexOf("berufe") > -1) {
      this.sortOptions = [];
      if(this.requirementsTestDone) {
        this.sortOptions.push({
          label: "Größte Übereinstimmung",
          value: "matching-desc",
          disabled: false
        });
      }

      this.sortOptions.push({
        label: "Längste Ausbildungszeit",
        value: "duration-asc",
        disabled: false
      },
      {
        label: "Kürzeste Ausbildungszeit",
        value: "duration-desc",
        disabled: false
      });

      this.sortOptions = [...this.sortOptions];
    }

    if(url.indexOf("jobboerse") > -1) {
      this.sortOptions = [
        {
          label: "Näheste Entfernung",
          value: "distance",
          disabled: !this.isLocationFilterJobSet
        },
      ];
    }
  }

  onSortingChange(event: any) {
    // this.selectLabel = event.target.options[event.target.selectedIndex].innerHTML;
    this.setSelectWidth(event);
    this.store.dispatch(setJobFilter({key: this.filterKey, value: event.target.value}))
  }

  setSelectWidth(event?: any) {
    window.requestAnimationFrame(() => {
      var select = this.selectElement.nativeElement as HTMLSelectElement;
      if(!select) {
        return
      }
      let labelWidth = select.closest(".sorting-dropdown")?.querySelector(".sorting-dropdown__label")?.clientWidth;
      if(!labelWidth) labelWidth = 0;
      var o = select.options[select.selectedIndex];
      if(!o) {
        return;
      }
      var s = document.createElement('span');
      s.textContent = o.textContent;
      var ostyles = getComputedStyle(o);
      s.style.fontFamily = ostyles.fontFamily;
      s.style.fontStyle = ostyles.fontStyle;
      s.style.fontWeight = ostyles.fontWeight;
      s.style.fontSize = ostyles.fontSize;
      document.body.appendChild(s);
      select.style.width = (s.offsetWidth + labelWidth + 5 + 2) + 'px';
      select.style.paddingLeft = (labelWidth + 5) + 'px';

      s.remove();
    })

  }
}
